'use client';

import { RootLoading } from '@workspace/ui';
import {
  isGuestOnlyRoute,
  isPublicRoute,
  subscribeToAuthTokenRefresh,
  subscribeToCrossAppAuthRefresh,
} from '@workspace/ui/lib';
import type { JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import {
  clearAuthCookies,
  refreshToken,
  syncAuthSessionFromCookies,
  syncAuthStateFromAccessToken,
} from '@/actions/mixed/auth';
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from '@/config/routes';
import { useAuthStore } from '@/stores/authStore';

const ONBOARDING_CHECK_KEY = 'onboarding_check_completed';

export default function AuthBootstrap({
  initialAccessToken,
  hasRefreshToken,
  children,
}: {
  initialAccessToken?: string;
  hasRefreshToken: boolean;
  children: React.ReactNode;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setIsAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);
  const setUser = useAuthStore((s) => s.setUser);
  const isLoading = useAuthStore((s) => s.isLoading);

  const _router = useRouter();
  const pathname = usePathname();

  useEffect(() => subscribeToCrossAppAuthRefresh(syncAuthSessionFromCookies), []);

  useEffect(
    () =>
      subscribeToAuthTokenRefresh((nextAccessToken) => {
        try {
          syncAuthStateFromAccessToken(nextAccessToken);
        } catch (error) {
          console.error('Failed to synchronize refreshed access token:', error);
        }
      }),
    []
  );

  useEffect(() => {
    // Don't trigger refresh for guest-only routes or when we have an access token
    if (isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname) || accessToken) {
      setIsLoading(false);
      return;
    }

    if (isPublicRoute(PUBLIC_ROUTES, pathname) && !initialAccessToken && !hasRefreshToken) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const now = Date.now();

    // If we have an initial token, decode it to check expiration
    if (initialAccessToken) {
      try {
        const decodedToken = jwtDecode<JwtPayload>(initialAccessToken);

        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds

        const user: JwtUser = {
          user: {
            id: Number(decodedToken.sub),
            first_name: decodedToken.user.first_name,
            last_name: decodedToken.user.last_name,
            email: decodedToken.user.email,
          },
          permissions: decodedToken.permissions,
          roles: decodedToken.roles,
          organisations: decodedToken.organisations ?? [],
          active_organisation: decodedToken.active_organisation ?? decodedToken.organisations?.[0],
          accessible_app: decodedToken.accessible_app,
          teams: decodedToken.teams,
        };

        // Only refresh if we're within 1 minute of expiration
        if (expirationTime - now > 60 * 1000) {
          if (abortController.signal.aborted) return;

          setAccessToken(initialAccessToken);

          setIsAuthenticated(true);

          setUser(user);
          setIsLoading(false);

          return;
        } else {
          refreshToken()
            .then((result) => {
              if (abortController.signal.aborted) return;

              if (!result.success) {
                // Refresh failed, clear auth and redirect
                setAccessToken(null);
                setIsAuthenticated(false);
                setUser(null);
                clearAuthCookies().finally(() => {
                  window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
                });
              }
              // On success, refreshToken() already updated the store
            })
            .catch((error) => {
              console.error('Failed to refresh token:', error);

              if (abortController.signal.aborted) return;

              // Handle error same as refresh failure
              setAccessToken(null);
              setIsAuthenticated(false);
              setUser(null);
              clearAuthCookies().finally(() => {
                window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
              });
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      } catch (error) {
        console.error('Failed to decode token:', error);

        if (abortController.signal.aborted) return;

        localStorage.removeItem(ONBOARDING_CHECK_KEY);
        setAccessToken(null);
        setIsAuthenticated(false);
        setUser(null);

        clearAuthCookies().finally(() => {
          window.location.href = '/login';
        });
      }
    } else {
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [
    pathname,
    accessToken,
    hasRefreshToken,
    setAccessToken,
    setIsAuthenticated,
    setIsLoading,
    setUser,
    initialAccessToken,
  ]);

  if (isLoading) {
    return <RootLoading />;
  }

  return children;
}
